/**
 * useMechatUnread — background watcher for new incoming mechat messages.
 *
 * Mounted once in App. Polls Hyperion for the active account's `senddm` history
 * and compares timestamps against the local read state. No decryption happens
 * here: counting unread only needs the action metadata, so the watcher works
 * without touching the private key and stays cheap.
 *
 * Poll interval is deliberately lazy — messages are transactions, nobody expects
 * instant delivery, and each poll is one Hyperion request.
 */

import { useCallback, useEffect, useRef } from 'react'
import { fetchMessages } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { countUnread, ensureReadStateInitialized } from '../lib/mechatReadState'

const POLL_MS = 60_000

export function useMechatUnread(): { refresh: () => Promise<void> } {
  const activeAccount = useWalletStore(s => s.activeAccount)
  const isSessionLocked = useWalletStore(s => s.isSessionLocked)
  const setMechatUnread = useAppStore(s => s.setMechatUnread)
  const blockedAccounts = useAppStore(s => s.blockedAccounts)
  const accountName = activeAccount?.name ?? ''
  const inFlight = useRef(false)

  const refresh = useCallback(async () => {
    if (!accountName || isSessionLocked || inFlight.current) return
    inFlight.current = true
    try {
      const all = await fetchMessages({ account: accountName, limit: 50 })
      // A blocked account must not light up the Chat tab.
      const messages = all.filter(m => !blockedAccounts.includes(m.from))
      const state = ensureReadStateInitialized(accountName, messages)
      const { total, byPeer } = countUnread(messages, state)
      setMechatUnread(total, byPeer)
    } catch {
      // Offline or Hyperion hiccup — keep the previous count rather than zeroing it.
    } finally {
      inFlight.current = false
    }
  }, [accountName, isSessionLocked, setMechatUnread, blockedAccounts])

  useEffect(() => {
    if (!accountName || isSessionLocked) {
      setMechatUnread(0, {})
      return
    }
    void refresh()
    const timer = setInterval(() => {
      if (!document.hidden) void refresh()
    }, POLL_MS)
    const onVisible = () => {
      if (!document.hidden) void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [accountName, isSessionLocked, refresh, setMechatUnread])

  return { refresh }
}
